import { useCallback } from 'react';
import { notification } from 'tea-component';
import { fetchReportExcel } from '@intelligence/services/common';
import { setIntelligenceReport, editIntelligenceReport } from '@home/services/studio';
import { IntelligenceReportDataInfo, ReportDateRanges } from '../type';

interface UseReportActionsParams {
  /** 格式化消息函数 */
  formatMessage: (descriptor: { id: string; }) => string;
  /** Studio Code */
  studioCode?: string;
  /** 数据变化回调 */
  onDataChange?: (key: string, value: any) => void;
  /** 报告模块分类 */
  category?: string;
}

interface UseReportActionsReturn {
  /** Excel下载处理函数 */
  handleDownloadExcel: (id: string, name: string) => Promise<boolean>;
  /** 确认保存处理函数；saveCategory 覆盖组件 props 上的 category（由调用方按当前 Tab 配置解析） */
  handleConfirmSave: (
    selectedReports: IntelligenceReportDataInfo[],
    reportDateRanges: ReportDateRanges,
    saveCategory?: string,
  ) => Promise<boolean>;
  handleEditReport: (params: {
    report: IntelligenceReportDataInfo;
    isEn: boolean;
    tags: string[];
    introduction: string;
  }) => Promise<boolean>;
}

/**
 * 报告操作相关的 Hook
 * 包含 Excel 下载和保存操作
 */
export const useReportActions = ({
  formatMessage,
  studioCode,
  onDataChange,
  category,
}: UseReportActionsParams): UseReportActionsReturn => {
  /** Excel 下载 */
  const handleDownloadExcel = useCallback(async (id: string, name: string): Promise<boolean> => {
    if (!id) {
      notification.warning({
        description: formatMessage({ id: 'dbh_studio_report_invalid_id' }),
        unique: true,
      });
      return false;
    }
    
    try {
      const res = await fetchReportExcel({ id, name });
      const blob = new Blob([res]);
      const a = document.createElement('a');
      a.style.display = 'none';
      a.href = URL.createObjectURL(blob);
      a.download = name;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(a.href); // 释放 URL 对象
      
      notification.success({
        description: formatMessage({ id: 'dbh_studio_report_download_success' }),
        unique: true,
      });
      return true;
    } catch (error) {
      console.error('下载失败:', error);
      notification.error({
        description: formatMessage({ id: 'dbh_studio_report_download_failed' }),
        unique: true,
      });
      return false;
    }
  }, [formatMessage]);

  /** 确认保存 */
  const handleConfirmSave = useCallback(async (
    selectedReports: IntelligenceReportDataInfo[],
    reportDateRanges: ReportDateRanges,
    saveCategory?: string,
  ): Promise<boolean> => {
    if (!studioCode) {
      notification.warning({
        description: formatMessage({ id: 'dbh_studio_report_invalid_studio_code' }),
        unique: true,
      });
      return false;
    }

    try {
      // 构造接口参数
      const reports = selectedReports.map((item, index) => {
        const dateRange = reportDateRanges[item.resource_id];
        const report: any = {
          resource_id: item.resource_id,
          start_time: dateRange?.startTime || '',
          end_time: dateRange?.endTime || '',
          sort: index + 1,
        };
        // 回传已有的 studio 编辑内容，避免先删后增导致丢失
        if (item.studio_introduction) {
          report.introduction = item.studio_introduction;
        }
        if (item.studio_introduction_en) {
          report.introduction_en = item.studio_introduction_en;
        }
        if (item.studio_tag) {
          report.tag = item.studio_tag;
        }
        if (item.studio_tag_en) {
          report.tag_en = item.studio_tag_en;
        }
        return report;
      });

      const params: any = {
        studio_code: studioCode,
        reports,
      };
      const resolvedCategory = saveCategory ?? category;
      if (resolvedCategory) {
        params.category = resolvedCategory;
      }

      await setIntelligenceReport(params);
      
      notification.success({
        description: formatMessage({ id: 'dbh_studio_funcom_save_modal_highlights_success' }),
        unique: true,
      });
      
      // 保存成功后更新refreshTimestamp触发Service重新请求报告列表
      if (onDataChange) {
        onDataChange('refreshTimestamp', Date.now());
      }
      
      return true;
    } catch (error) {
      console.error('保存失败:', error);
      notification.error({
        description: formatMessage({ id: 'dbh_studio_funcom_save_modal_highlights_failed' }),
        unique: true,
      });
      return false;
    }
  }, [formatMessage, studioCode, onDataChange, category]);

  /**
   * 编辑单个报告的标签和介绍
   * 同时传中英文 4 个字段，当前语言用编辑值，另一语言用现有值
   */
  const handleEditReport = useCallback(async (params: {
    report: IntelligenceReportDataInfo;
    isEn: boolean;
    tags: string[];
    introduction: string;
  }): Promise<boolean> => {
    if (!studioCode) {
      notification.warning({
        description: formatMessage({ id: 'dbh_studio_report_invalid_studio_code' }),
        unique: true,
      });
      return false;
    }

    try {
      const { report } = params;
      const apiParams: any = {
        studio_code: studioCode,
        resource_id: report.resource_id,
      };

      if (params.isEn) {
        apiParams.introduction_en = params.introduction;
        apiParams.tag_en = params.tags;
      } else {
        apiParams.introduction = params.introduction;
        apiParams.tag = params.tags;
      }

      await editIntelligenceReport(apiParams);

      notification.success({
        description: formatMessage({ id: 'dbh_studio_funcom_edit_modal_success' }),
        unique: true,
      });

      if (onDataChange) {
        onDataChange('refreshTimestamp', Date.now());
      }

      return true;
    } catch (error) {
      console.error('编辑报告失败:', error);
      notification.error({
        description: formatMessage({ id: 'dbh_studio_funcom_edit_modal_failed' }),
        unique: true,
      });
      return false;
    }
  }, [formatMessage, studioCode, onDataChange]);

  return {
    handleDownloadExcel,
    handleConfirmSave,
    handleEditReport,
  };
};
